import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar from '../../components/FilterBar';
import { nameOnlyFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal } from '../../components/ui';
import { SkeletonTable } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/PageHeader';
import CapacityBadge from '../../components/CapacityBadge';
import { seasonLabel } from '../../utils/seasonLabel';
import { translateError } from '../../utils/errorMessages';

interface ICourse {
  _id: string | number;
  name: string;
  description?: string;
  activityId?: number | null;
  capacity?: number | null;
  enrollmentPolicy: 'open' | 'approval';
  isActive: boolean;
  enrollmentCount?: number;
  termStart?: string | null;
  termEnd?: string | null;
  isExpired?: boolean;
}

interface IActivity {
  _id: string | number;
  name: string;
}

const CoursesListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [activities, setActivities] = useState<IActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filters, setFilters] = useState<FilterValue[]>([]);

  const canCreate = hasPermission('activities:write');
  const canEdit = hasPermission('activities:write');
  const canDelete = hasPermission('activities:write');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [coursesRes, activitiesRes] = await Promise.all([
        api.courses.list(),
        api.activities.list().catch(() => ({ data: [] })),
      ]);
      setCourses(coursesRes.data as ICourse[]);
      setActivities(activitiesRes.data as IActivity[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activityNameById = useMemo(() => {
    const map = new Map<string, string>();
    activities.forEach((a) => map.set(String(a._id), a.name));
    return map;
  }, [activities]);

  const handleDeleteClick = (course: ICourse) => {
    if (!canDelete) return;
    setDeleteId(course._id);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId === null) return;
    setDeleteLoading(true);
    try {
      await api.courses.delete(Number(deleteId));
      addToast(t('courses.deleted'), 'success');
      fetchData();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const getPolicyBadge = (policy: string) => {
    const isOpen = policy === 'open';
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '0.2rem 0.6rem',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 500,
          backgroundColor: isOpen ? 'rgba(40, 167, 69, 0.15)' : 'rgba(0, 123, 255, 0.15)',
          color: isOpen ? '#28a745' : '#007bff',
        }}
      >
        {isOpen ? t('courses.enrollmentPolicyOpen') : t('courses.enrollmentPolicyApproval')}
      </span>
    );
  };

  const columns: Column<ICourse>[] = useMemo(() => [
    {
      header: t('courses.name'),
      // Expired courses are dimmed (not hidden) so a season's history stays
      // visible while reading as inactive.
      render: (course) => (
        <span style={{ opacity: course.isExpired ? 0.55 : 1 }}>{course.name}</span>
      ),
      sortValue: (course) => course.name ?? null,
    },
    {
      header: t('courseSchedule.season'),
      render: (course) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            opacity: course.isExpired ? 0.7 : 1,
          }}
        >
          {seasonLabel(course.termStart, course.termEnd, t, i18n.language)}
          {course.isExpired && (
            <span
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                padding: '0.1rem 0.5rem',
                borderRadius: '12px',
                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                color: 'var(--danger, #dc3545)',
              }}
            >
              {t('courses.expired')}
            </span>
          )}
        </span>
      ),
      sortValue: (course) => course.termStart ?? null,
    },
    {
      header: t('courses.activity'),
      render: (course) =>
        course.activityId != null ? (activityNameById.get(String(course.activityId)) || '-') : '-',
      sortValue: (course) =>
        course.activityId != null ? (activityNameById.get(String(course.activityId)) || null) : null,
    },
    {
      header: t('courses.enrollmentPolicy'),
      render: (course) => getPolicyBadge(course.enrollmentPolicy),
      sortValue: (course) => course.enrollmentPolicy ?? null,
    },
    {
      header: t('courses.capacity'),
      render: (course) => (course.capacity != null ? String(course.capacity) : 'n/a'),
      sortValue: (course) => course.capacity ?? null,
    },
    {
      header: t('courseEnrollments.title'),
      render: (course) =>
        course.capacity != null ? (
          <CapacityBadge enrolled={course.enrollmentCount ?? 0} capacity={course.capacity} showExact />
        ) : (
          String(course.enrollmentCount ?? 0)
        ),
      sortValue: (course) => course.enrollmentCount ?? 0,
    },
    {
      header: t('courses.isActive'),
      render: (course) => (course.isActive ? t('common.yes') : t('common.no')),
      sortValue: (course) => (course.isActive ? 1 : 0),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, i18n.language, activityNameById]);

  const filteredCourses = useMemo(() => applyFilters(courses, filters), [courses, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  const createAction = canCreate ? (
    <Link to="/admin/courses/create" className="btn btn--primary btn--md">
      {t('courses.create')}
    </Link>
  ) : undefined;

  return (
    <div className="admin-list-page">
      <PageHeader title={t('courses.title')} action={createAction} />

      {loading ? (
        <SkeletonTable rows={5} columns={6} />
      ) : courses.length === 0 ? (
        <EmptyState
          title={t('courses.noCourses')}
          description={t('courses.emptyDescription')}
          action={canCreate ? { label: t('courses.create'), href: '/admin/courses/create' } : undefined}
        />
      ) : (
        <>
          <FilterBar fields={nameOnlyFields} onFilterChange={handleFilterChange} />
          <DataTable
            data={filteredCourses}
            columns={columns}
            editLink={canEdit ? (course) => `/admin/courses/${course._id}/edit` : undefined}
            onDelete={canDelete ? handleDeleteClick : undefined}
          />
        </>
      )}

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('courses.deleteTitle')}
        message={t('courses.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default CoursesListPage;
